import React, {cloneElement, Component} from 'react'
import {CSSTransitionGroup} from 'react-transition-group'
import {DragDropContext, Droppable, Draggable} from 'react-beautiful-dnd'
import propTypes from 'prop-types'
import {get, isEmpty, isNil} from 'lodash'
import Row from './row'
import DragLayer from 'components/dragLayer'
import {prefix} from 'helpers/component'
import {getDraggableKey, getDroppableKey, calculateNewIndex} from 'helpers/pivotTable/dragDrop'

const getClassName = prefix('pivotTable-grid-')

class Grid extends Component {
  state = {
    draggingKey: null,
  }

  onDragStart = ({draggableId}) => {
    this.setState({draggingKey: draggableId})
  }

  onDragEnd = (result) => {
    const {data, onRowMove} = this.props

    this.setState({draggingKey: null})

    const newIndex = calculateNewIndex(result)
    if (newIndex === null) return

    const sourceIndex = result.source.index
    if (sourceIndex === newIndex) return

    const entity = data[sourceIndex]
    onRowMove(entity, sourceIndex, newIndex)
  }

  getSelected = (e, index, level, isFixed, onCanSelect, onIsRowSelected) => {
    let canSelect = !isFixed
    if (canSelect && onCanSelect) canSelect = onCanSelect(e, index, level)
    return canSelect && (
      onIsRowSelected
        ? onIsRowSelected(e, index, level)
        : get(e, 'selected', false)
    )
  }

  getRow = (e, index, isLast, isFixed, columns) => {
    const {
      columnSizes,
      highlight,
      rowStyle,
      controlColumns,
      canReorderItems,
      showSubData,
      subDataProp,
      expandedSubData,
      level,
      startIndex,
      onIsRowSelected,
      onSelect,
      onCanSelect,
      onExpandSubData,
      onCollapseSubData,
      hideFixedRowAddButton,
      actionColumns,
      onGetShowRowHoverHint,
      onGetRowHoverHintMessage,
    } = this.props
    let ht = null
    let style = null
    if (highlight) ht = highlight(e)
    if (rowStyle) style = rowStyle(e)

    const selected = this.getSelected(e, index, level, isFixed, onCanSelect, onIsRowSelected)
    const hasSubData = (get(e, subDataProp) || []).length > 0
    let subDataExpanded = (expandedSubData[level] === index)

    if (subDataExpanded && !hasSubData) {
      subDataExpanded = false
      onCollapseSubData(level, index) // eek!
    }

    const subDataHighlighted = (subDataExpanded && isNil(expandedSubData[level + 1]))
      || (!isNil(expandedSubData[level - 1]) && isNil(expandedSubData[level]))
    const hidden = !subDataHighlighted
      && !isFixed
      && Object.keys(expandedSubData).some(key => !isNil(expandedSubData[key]))

    const row = (
      <Row
        key={index}
        index={index}
        isLast={isLast}
        level={level}
        entity={e}
        highlight={ht}
        hidden={hidden}
        isFixed={isFixed}
        style={style}
        columns={columns}
        columnSizes={columnSizes}
        controlColumns={controlColumns}
        actionColumns={actionColumns}
        isSelected={selected}
        showSubData={showSubData}
        subDataProp={subDataProp}
        subDataExpanded={subDataExpanded}
        subDataHighlighted={subDataHighlighted}
        onToggleSubData={() =>
          subDataExpanded ? onCollapseSubData(level, index) : onExpandSubData(level, index)}
        onCollapseSubData={onCollapseSubData}
        hideFixedRowAddButton={hideFixedRowAddButton}
        onSelect={onSelect == null ? null : () => onSelect(e, index)}
        onGetShowRowHoverHint={onGetShowRowHoverHint}
        onGetRowHoverHintMessage={onGetRowHoverHintMessage}
      />
    )

    if (canReorderItems && !isFixed) {
      const draggableKey = getDraggableKey(e, startIndex, index)
      return (
        <Draggable
          key={draggableKey}
          draggableId={draggableKey}
          index={index}
        >
          {(provided, snapshot) =>
            cloneElement(row, {
              innerRef: provided.innerRef,
              draggableProps: provided.draggableProps,
              dragHandleProps: provided.dragHandleProps,
              isDragging: snapshot.isDragging,
            })
          }
        </Draggable>
      )
    }

    return row
  }

  getSubDataGrid = (data, index, _isLastRow) => {
    const {level, isLastRow, isSubGrid, startIndex, ...otherProps} = this.props

    const parentEntity = this.props.data[index]
    const parentId = parentEntity.id || parentEntity.guid || `idx-${startIndex + index}`

    return (
      <Grid
        key={`sub_${index}`}
        {...otherProps}
        data={data}
        startIndex={0}
        level={level + 1}
        parentId={parentId}
        showSubData
        fixedDataTop={null}
        fixedDataBottom={null}
        isLastRow={_isLastRow && (!isSubGrid || isLastRow)}
        isSubGrid
      />
    )
  }

  getSubData = (entity) => {
    const {showSubData, subDataProp} = this.props
    if (showSubData && subDataProp) {
      const subData = get(entity, subDataProp)
      return !isEmpty(subData) ? subData : null
    }
    return null
  }

  render() {
    const {
      allColumns,
      visibleColumns,
      data,
      startIndex,
      fixedDataTop,
      fixedDataBottom,
      stickyTop,
      stickyBottom,
      expandedSubData,
      isSubGrid,
      isLastRow,
      level,
      canReorderItems,
      parentId,
    } = this.props

    const columns = visibleColumns.map(c => allColumns[c])

    let rowIndex = startIndex
    let fixedTop
    if (fixedDataTop) {
      const classes = ['fixedDataTop']
      let style = {}
      if (!isNil(stickyTop)) {
        classes.push('fixedDataTop-sticky')
        style = {top: stickyTop}
      }

      const topRows = fixedDataTop.map((e, idx) =>
        this.getRow(e, rowIndex++, idx === fixedDataTop.length - 1, true, columns))
      fixedTop = <div className={getClassName(classes)} style={style}>{topRows}</div>
    }

    const content = []
    data.forEach((e, idx) => {
      content.push(this.getRow(e, rowIndex, idx === data.length - 1, false, columns))

      const subData = this.getSubData(e)
      if (!isNil(subData)) {
        const expanded = (expandedSubData[level] === rowIndex)
        const subGrid = expanded
          ? this.getSubDataGrid(subData, idx, (idx === data.length - 1))
          : null
        content.push(
          <CSSTransitionGroup
            key={`css-transition-group-${idx}-${e.id}`}
            transitionName={getClassName('sub')}
            transitionEnterTimeout={500}
            transitionLeaveTimeout={500}>
            {subGrid}
          </CSSTransitionGroup>)
      }
      rowIndex++
    })

    let fixedBottom
    if (fixedDataBottom) {

      const classes = ['fixedDataBottom']
      let style = {}
      if (!isNil(stickyBottom)) {
        classes.push('fixedDataBottom-sticky')
        style = {bottom: stickyBottom}
      }
      const bottomRows = fixedDataBottom.map((e, idx) =>
        this.getRow(e, rowIndex++, idx === fixedDataBottom.length - 1, true, columns))
      fixedBottom = <div className={getClassName(classes)} style={style}>{bottomRows}</div>
    }

    let gutterLines
    if (isSubGrid) {
      const gutterClasses = ['gutterLine', 'gutterLine-top']
      if (isNil(expandedSubData[level]) || !isNil(expandedSubData[level + 1]))
        gutterClasses.push('gutterLine-highlighted')
      gutterLines = [<div key="top" className={getClassName(gutterClasses)}/>]
      if (!isLastRow)
        gutterLines.push(<div key="btm" className={getClassName(['gutterLine', 'gutterLine-bottom'])}/>)
    }

    const gridContent = canReorderItems
      ? (
        <DragDropContext
          onDragStart={this.onDragStart}
          onDragEnd={this.onDragEnd}
        >
          <Droppable
            droppableId={getDroppableKey(level, parentId)}
            renderClone={(provided, snapshot, rubric) => {
              const entity = data[rubric.source.index]
              const index = startIndex + rubric.source.index
              const {
                columnSizes,
                highlight,
                rowStyle,
                controlColumns,
                actionColumns,
                showSubData,
                subDataProp,
                expandedSubData,
                onIsRowSelected,
                onSelect,
                onCanSelect,
                onExpandSubData,
                onCollapseSubData,
                onGetShowRowHoverHint,
                onGetRowHoverHintMessage,
              } = this.props

              let ht = null
              let style = null
              if (highlight) ht = highlight(entity)
              if (rowStyle) style = rowStyle(entity)

              const selected = this.getSelected(entity, index, level, false, onCanSelect, onIsRowSelected)
              const hasSubData = (get(entity, subDataProp) || []).length > 0
              let subDataExpanded = (expandedSubData[level] === index)

              if (subDataExpanded && !hasSubData) {
                subDataExpanded = false
              }

              const subDataHighlighted = (subDataExpanded && isNil(expandedSubData[level + 1]))
                || (!isNil(expandedSubData[level - 1]) && isNil(expandedSubData[level]))

              return (
                <Row
                  innerRef={provided.innerRef}
                  draggableProps={provided.draggableProps}
                  dragHandleProps={provided.dragHandleProps}
                  isDragging={snapshot.isDragging}
                  key={index}
                  index={index}
                  isLast={false}
                  level={level}
                  entity={entity}
                  highlight={ht}
                  hidden={false}
                  isFixed={false}
                  style={style}
                  columns={columns}
                  columnSizes={columnSizes}
                  controlColumns={controlColumns}
                  actionColumns={actionColumns}
                  isSelected={selected}
                  showSubData={showSubData}
                  subDataProp={subDataProp}
                  subDataExpanded={subDataExpanded}
                  subDataHighlighted={subDataHighlighted}
                  onToggleSubData={() =>
                    subDataExpanded ? onCollapseSubData(level, index) : onExpandSubData(level, index)}
                  onCollapseSubData={onCollapseSubData}
                  hideFixedRowAddButton={false}
                  onSelect={onSelect == null ? null : () => onSelect(entity, index)}
                  onGetShowRowHoverHint={onGetShowRowHoverHint}
                  onGetRowHoverHintMessage={onGetRowHoverHintMessage}
                />
              )
            }}
          >
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={getClassName(snapshot.isDraggingOver ? 'content-dropping' : 'content')}
              >
                {content}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )
      : content

    const gridElement = (
      <div key={'grid'} className={getClassName(isSubGrid ? 'sub' : 'root')}>
        {gutterLines}
        {fixedTop}
        {gridContent}
        {fixedBottom}
      </div>
    )

    return canReorderItems
      ? gridElement
      : (
        <DragLayer preventUpdateWhileDragging={false} className={getClassName('container')}>
          {gridElement}
        </DragLayer>
      )
  }
}

Grid.propTypes = {
  data: propTypes.arrayOf(propTypes.object).isRequired,
  startIndex: propTypes.number.isRequired,
  fixedDataTop: propTypes.arrayOf(propTypes.object),
  fixedDataBottom: propTypes.arrayOf(propTypes.object),
  allColumns: propTypes.object.isRequired,
  visibleColumns: propTypes.arrayOf(propTypes.string).isRequired,
  columnSizes: propTypes.object.isRequired,
  controlColumns: propTypes.arrayOf(propTypes.shape({
    size: propTypes.number.isRequired,
  })),
  actionColumns: propTypes.arrayOf(propTypes.shape({
    size: propTypes.number.isRequired,
  })),
  rowStyle: propTypes.func,
  stickyTop: propTypes.number,
  stickyBottom: propTypes.number,
  canReorderItems: propTypes.bool.isRequired,
  highlight: propTypes.func,
  areAllRowsSelected: propTypes.bool,
  showSubData: propTypes.bool,
  subDataProp: propTypes.string,
  isSubGrid: propTypes.bool,
  isLastRow: propTypes.bool,
  level: propTypes.number,
  parentId: propTypes.oneOfType([propTypes.string, propTypes.number]),
  expandedSubData: propTypes.object,
  hideFixedRowAddButton: propTypes.bool,
  onVisibleColumnsChanged: propTypes.func.isRequired,
  onSelect: propTypes.func,
  onCanSelect: propTypes.func,    // (entity: object, rowIndex: number): bool
  onIsRowSelected: propTypes.func,
  onRowMove: propTypes.func,
  onExpandSubData: propTypes.func,
  onCollapseSubData: propTypes.func,
  onGetShowRowHoverHint: propTypes.func,
  onGetRowHoverHintMessage: propTypes.func,
}

Grid.defaultProps = {
  startIndex: 0,
  fixedDataTop: [],
  fixedDataBottom: [],
  showSubData: false,
  isSubGrid: false,
  level: 0,
}

export default Grid
